import React, { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { removeFromCart, updateQuantity } from '../store/slices/cartSlice';
import CartItemModal from './CartItemModal';
import { Trash } from 'lucide-react';

const CartItemComponent = ({ item }) => {
  const dispatch = useDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Determine if any extras have a price and a positive quantity
  const hasPaidExtras = useMemo(() => {
    if (!item.extras) return false;
    return Object.entries(item.extras).some(([_, extras]) =>
      extras.some(extra => extra.price > 0 && extra.quantity > 0)
    );
  }, [item.extras]);

  // Format item details (name and type)
  const formatItemDetails = () => {
    const parts = [item.name];
    if (item.type) parts.push(`(${item.type})`);
    return parts.join(' ');
  };

  // Render extras list if any exist
  const renderExtras = () => {
    if (!item.extras) return null;
    return Object.entries(item.extras)
      .map(([category, extras]) => {
        const validExtras = extras.filter(extra => extra.quantity > 0);
        if (validExtras.length === 0) return null;
        return (
          <div
            key={category}
            className="extras-section mt-2 p-3 bg-gray-900 rounded-lg shadow-lg"
          >
            <h4 className="text-lg font-bold text-red-500 mb-1 border-b border-red-500 pb-1">
              {category.charAt(0).toUpperCase() + category.slice(1)}
            </h4>
            <ul className="space-y-1">
              {validExtras.map((extra, index) => (
                <li key={index} className="extra-item text-white flex justify-between">
                  <span>{extra.name}</span>
                  <span>Qty: {extra.quantity}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })
      .filter(Boolean);
  };

  // Render chef comment if provided
  const renderChefComment = () => {
    if (item.chefComment && item.chefComment.trim() !== '') {
      return (
        <div className="mt-3 p-3 bg-red-100 rounded-lg border-l-4 border-red-500 flex items-center">
          <span className="mr-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 text-red-500"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
          </span>
          <span className="font-semibold text-red-700">P.S.: {item.chefComment}</span>
        </div>
      );
    }
    return null;
  };

  // Decrease quantity but not below 1
  const handleDecrease = () => {
    const newQuantity = Math.max(1, item.quantity - 1);
    dispatch(updateQuantity({ id: item.id, quantity: newQuantity }));
  };

  // Increase quantity
  const handleIncrease = () => {
    dispatch(updateQuantity({ id: item.id, quantity: item.quantity + 1 }));
  };

  return (
    <div className="cart-item bg-gradient-to-r from-gray-800 to-black p-6 rounded-xl shadow-xl border border-red-500 mb-4">
      {/* Item Details and Extras */}
      <div className="w-full">
        <h3 className="text-2xl text-red-500 font-bold mb-2">
          {formatItemDetails()}
        </h3>
        {renderExtras()}
      </div>

      {/* Responsive Layout */}
      {/* Large screens: total on left, quantity & actions on right */}
      <div className="hidden md:flex justify-between items-center mt-4">
        {/* Left: Total */}
        <p className="text-white text-xl">
          Total: <span className="font-bold">₦{item.totalPrice.toLocaleString()}</span>
        </p>
        {/* Right: Quantity control and action buttons */}
        <div className="flex items-center space-x-4">
          <div className="quantity-control flex items-center">
            <label className="text-white mr-2 text-lg">Quantity:</label>
            <div className="flex items-center bg-gray-900 rounded overflow-hidden">
              <button
                onClick={handleDecrease}
                className="px-4 py-2 bg-gray-800 text-white hover:bg-gray-700 transition-colors"
              >
                -
              </button>
              <span className="px-6 py-2 bg-gray-900 text-white">
                {item.quantity}
              </span>
              <button
                onClick={handleIncrease}
                className="px-4 py-2 bg-gray-800 text-white hover:bg-gray-700 transition-colors"
              >
                +
              </button>
            </div>
          </div>
          {hasPaidExtras && (
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-5 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Edit Extras
            </button>
          )}
          <button
            onClick={() => dispatch(removeFromCart(item.id))}
            className="p-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            aria-label="Remove Item"
          >
            <Trash size={24} />
          </button>
        </div>
      </div>

      {/* Small screens */}
      <div className="md:hidden">
        {/* Quantity control remains as before */}
        <div className="quantity-control mt-4 flex items-center">
          <label className="text-white mr-4 text-lg">Quantity:</label>
          <div className="flex items-center bg-gray-900 rounded overflow-hidden">
            <button
              onClick={handleDecrease}
              className="px-4 py-2 bg-gray-800 text-white hover:bg-gray-700 transition-colors"
            >
              -
            </button>
            <span className="px-6 py-2 bg-gray-900 text-white">
              {item.quantity}
            </span>
            <button
              onClick={handleIncrease}
              className="px-4 py-2 bg-gray-800 text-white hover:bg-gray-700 transition-colors"
            >
              +
            </button>
          </div>
        </div>

        {/* Conditionally render Total and Trash button row */}
        {!hasPaidExtras ? (
          // For items WITHOUT extras: Total and Trash on same row
          <div className="flex items-center justify-between mt-4">
            <p className="text-white text-xl">
              Total: <span className="font-bold">₦{item.totalPrice.toLocaleString()}</span>
            </p>
            <button
              onClick={() => dispatch(removeFromCart(item.id))}
              className="p-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              aria-label="Remove Item"
            >
              <Trash size={24} />
            </button>
          </div>
        ) : (
          // For items WITH extras: render Total and then actions separately
          <>
            <p className="text-white mt-4 text-xl">
              Total: <span className="font-bold">₦{item.totalPrice.toLocaleString()}</span>
            </p>
            <div className="w-full flex justify-between items-center mt-6">
              <div className="flex space-x-4">
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="px-5 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Edit Extras
                </button>
                <button
                  onClick={() => dispatch(removeFromCart(item.id))}
                  className="p-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  aria-label="Remove Item"
                >
                  <Trash size={24} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Chef comment */}
      {renderChefComment()}

      {/* Modal for editing extras */}
      {isModalOpen && hasPaidExtras && (
        <CartItemModal item={item} onClose={() => setIsModalOpen(false)} />
      )}
    </div>
  );
};

export default CartItemComponent;
